import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Button, VStack, HStack, Input, Box, Text, Badge,
  useToast, Flex, FormControl, FormLabel, Tabs, TabList, Tab, Wrap, WrapItem,
  List, ListItem, Spinner,
} from '@chakra-ui/react';
import { FiSearch, FiX, FiMail } from 'react-icons/fi';
import api from '../api';

// Search component for selecting multiple companies or contacts
function MultiSearch({ type, selected, onAdd, onRemove }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const debounce = useRef(null);

  const search = useCallback(async (q) => {
    if (q.length < 1) { setResults([]); setOpen(false); return; }
    try {
      const endpoint = type === 'company' ? '/companies/search' : '/contacts/search';
      const { data } = await api.get(`${endpoint}?q=${encodeURIComponent(q)}`);
      setResults(data);
      setOpen(true);
    } catch (e) {}
  }, [type]);

  const handleChange = (e) => {
    setQuery(e.target.value);
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => search(e.target.value), 200);
  };

  const handleSelect = (item) => {
    onAdd(item);
    setQuery('');
    setOpen(false);
  };

  const displayName = (item) => {
    if (type === 'company') return item.name;
    return `${item.first_name} ${item.last_name || ''}`.trim() + (item.company_name ? ` (${item.company_name})` : '');
  };

  return (
    <Box>
      {/* Selected tags */}
      {selected.length > 0 && (
        <Wrap spacing={1} mb={2}>
          {selected.map(item => (
            <WrapItem key={item.id}>
              <Badge colorScheme={type === 'company' ? 'teal' : 'purple'} fontSize="10px"
                cursor="pointer" display="flex" alignItems="center" gap={1}
                onClick={() => onRemove(item.id)}>
                {displayName(item)}
                <FiX size={10} />
              </Badge>
            </WrapItem>
          ))}
        </Wrap>
      )}

      {/* Search input */}
      <Box position="relative">
        <Input
          size="sm"
          value={query}
          onChange={handleChange}
          onBlur={() => setTimeout(() => setOpen(false), 200)}
          placeholder={type === 'company' ? 'Search companies...' : 'Search contacts...'}
          fontSize="12px"
        />
        {open && results.length > 0 && (
          <Box position="absolute" top="100%" left={0} right={0} bg="white" border="1px" borderColor="gray.200"
            borderRadius="md" boxShadow="lg" zIndex={200} maxH="200px" overflowY="auto">
            <List spacing={0}>
              {results.filter(r => !selected.find(s => s.id === r.id)).map(item => (
                <ListItem key={item.id} px={3} py={1.5} fontSize="12px" cursor="pointer"
                  _hover={{ bg: 'blue.50' }} onClick={() => handleSelect(item)}>
                  <Text fontWeight="500">{displayName(item)}</Text>
                </ListItem>
              ))}
            </List>
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default function EngagementModal({ isOpen, onClose }) {
  const [searchType, setSearchType] = useState(0); // 0=company, 1=people
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [comms, setComms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedComm, setSelectedComm] = useState(null);

  const handleSearch = async () => {
    const companyIds = selectedCompanies.map(c => c.id);
    const contactIds = selectedContacts.map(c => c.id);

    if (companyIds.length === 0 && contactIds.length === 0) return;

    setLoading(true);
    setHasSearched(true);
    setSelectedComm(null);
    try {
      const params = new URLSearchParams();
      if (companyIds.length > 0) params.set('company_ids', companyIds.join(','));
      if (contactIds.length > 0) params.set('contact_ids', contactIds.join(','));
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);

      const { data } = await api.get(`/communications?${params.toString()}`);
      setComms(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setSelectedCompanies([]);
    setSelectedContacts([]);
    setDateFrom('');
    setDateTo('');
    setComms([]);
    setHasSearched(false);
    setSelectedComm(null);
  };

  useEffect(() => {
    if (isOpen) handleReset();
  }, [isOpen]);

  const methodColors = { email: 'blue', phone: 'green', meeting: 'purple', web_lead: 'cyan', chatbot: 'orange', whatsapp: 'green', in_person: 'pink', media_press: 'red', social_media: 'teal', other: 'gray' };
  const methodLabels = { email: 'Email', phone: 'Phone', meeting: 'Meeting', web_lead: 'Web Lead', chatbot: 'Chat Bot', whatsapp: 'WhatsApp', in_person: 'In Person', media_press: 'Media/Press', social_media: 'Social Media', other: 'Other' };
  const ml = (m) => methodLabels[m] || m;

  const formatDateTime = (comm) => {
    const d = new Date(comm.comm_date).toLocaleDateString('en-GB');
    const t = comm.comm_time ? comm.comm_time.slice(0, 5) : '';
    return t ? `${d} ${t}` : d;
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxH="85vh">
        <ModalHeader fontSize="md">
          <Flex align="center" gap={2}>
            <FiMail />
            View Engagement
          </Flex>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {/* Search panel */}
          {!selectedComm && (
            <Box bg="gray.50" p={3} borderRadius="md" mb={4}>
              <VStack spacing={3} align="stretch">
                <Tabs size="sm" index={searchType} onChange={setSearchType} variant="soft-rounded" colorScheme="brand">
                  <TabList>
                    <Tab fontSize="12px">Company</Tab>
                    <Tab fontSize="12px">People</Tab>
                  </TabList>
                </Tabs>

                {searchType === 0 && (
                  <MultiSearch
                    type="company"
                    selected={selectedCompanies}
                    onAdd={(co) => setSelectedCompanies(prev => [...prev, co])}
                    onRemove={(id) => setSelectedCompanies(prev => prev.filter(c => c.id !== id))}
                  />
                )}

                {searchType === 1 && (
                  <MultiSearch
                    type="contact"
                    selected={selectedContacts}
                    onAdd={(ct) => setSelectedContacts(prev => [...prev, ct])}
                    onRemove={(id) => setSelectedContacts(prev => prev.filter(c => c.id !== id))}
                  />
                )}

                <HStack spacing={2}>
                  <FormControl flex={1}>
                    <FormLabel fontSize="xs" mb={0.5}>Date From</FormLabel>
                    <Input size="xs" type="date" value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)} />
                  </FormControl>
                  <FormControl flex={1}>
                    <FormLabel fontSize="xs" mb={0.5}>Date To</FormLabel>
                    <Input size="xs" type="date" value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)} />
                  </FormControl>
                </HStack>

                <HStack spacing={2}>
                  <Button size="sm" leftIcon={<FiSearch />} bg="brand.600" color="white"
                    _hover={{ bg: 'brand.700' }} onClick={handleSearch}
                    isDisabled={selectedCompanies.length === 0 && selectedContacts.length === 0}
                    isLoading={loading}>
                    Search
                  </Button>
                  <Button size="sm" variant="ghost" onClick={handleReset}>Reset</Button>
                </HStack>
              </VStack>
            </Box>
          )}

          {/* Communication detail view */}
          {selectedComm && (
            <Box>
              <Button size="xs" variant="ghost" onClick={() => setSelectedComm(null)} mb={2}>
                ← Back to results
              </Button>
              <Box bg="white" p={4} border="1px" borderColor="gray.200" borderRadius="md">
                <Text fontSize="md" fontWeight="600" mb={2}>{selectedComm.subject || '(no subject)'}</Text>
                <HStack fontSize="11px" color="gray.500" spacing={3} mb={1}>
                  <Text>{formatDateTime(selectedComm)}</Text>
                  <Badge size="sm" colorScheme={methodColors[selectedComm.method]} fontSize="9px">{ml(selectedComm.method)}</Badge>
                  <Badge size="sm" variant="outline" fontSize="9px"
                    colorScheme={selectedComm.direction === 'inbound' ? 'green' : 'blue'}>
                    {selectedComm.direction}
                  </Badge>
                  {selectedComm.purpose_name && (
                    <Badge size="sm" colorScheme="yellow" fontSize="9px">{selectedComm.purpose_name}</Badge>
                  )}
                </HStack>
                <HStack fontSize="11px" color="gray.600" spacing={2} mb={3} flexWrap="wrap">
                  {(selectedComm.company_name || selectedComm.contact_company_name) && (
                    <Badge colorScheme="teal" fontSize="9px">{selectedComm.company_name || selectedComm.contact_company_name}</Badge>
                  )}
                  {selectedComm.contact_first_name && (
                    <Badge colorScheme="purple" fontSize="9px">
                      {selectedComm.contact_first_name} {selectedComm.contact_last_name || ''}
                    </Badge>
                  )}
                  {selectedComm.project_name && (
                    <Badge colorScheme="orange" fontSize="9px">Project: {selectedComm.project_name}</Badge>
                  )}
                </HStack>
                <Text fontSize="12px" color="gray.700" whiteSpace="pre-wrap" lineHeight="1.6">
                  {selectedComm.body || '(no content)'}
                </Text>
                {selectedComm.created_by_name && (
                  <Text fontSize="10px" color="gray.400" mt={3}>Logged by {selectedComm.created_by_name}</Text>
                )}
              </Box>
            </Box>
          )}

          {/* Results list */}
          {!selectedComm && hasSearched && (
            loading ? (
              <Flex justify="center" py={6}><Spinner size="sm" /></Flex>
            ) : comms.length === 0 ? (
              <Text fontSize="sm" color="gray.500" textAlign="center" py={6}>
                No communications found for the selected criteria.
              </Text>
            ) : (
              <VStack spacing={1} align="stretch">
                <Text fontSize="11px" color="gray.500" mb={1}>{comms.length} result{comms.length !== 1 ? 's' : ''}</Text>
                {comms.map(comm => (
                  <Box key={comm.id} p={2} border="1px" borderColor="gray.100" borderRadius="md"
                    cursor="pointer" _hover={{ bg: 'gray.50' }} onClick={() => setSelectedComm(comm)}>
                    <Flex align="center" gap={2} fontSize="12px">
                      <Text fontWeight="500" color="gray.500" w="105px" flexShrink={0} fontSize="10px">
                        {formatDateTime(comm)}
                      </Text>
                      <Badge size="sm" colorScheme={methodColors[comm.method]} fontSize="8px">{ml(comm.method)}</Badge>
                      <Badge size="sm" variant="outline" fontSize="8px"
                        colorScheme={comm.direction === 'inbound' ? 'green' : 'blue'}>
                        {comm.direction === 'inbound' ? '←' : '→'}
                      </Badge>
                      {comm.purpose_name && (
                        <Badge size="sm" colorScheme="yellow" fontSize="8px">{comm.purpose_name}</Badge>
                      )}
                      <Text fontWeight="500" noOfLines={1} flex={1}>{comm.subject || '(no subject)'}</Text>
                    </Flex>
                    <Flex fontSize="10px" color="gray.400" ml="105px" mt={0.5} gap={2}>
                      {(comm.company_name || comm.contact_company_name) && (
                        <Text>{comm.company_name || comm.contact_company_name}</Text>
                      )}
                      {comm.contact_first_name && (
                        <Text>• {comm.contact_first_name} {comm.contact_last_name || ''}</Text>
                      )}
                      {comm.project_name && (
                        <Text>• {comm.project_name}</Text>
                      )}
                    </Flex>
                  </Box>
                ))}
              </VStack>
            )
          )}
        </ModalBody>
        <ModalFooter>
          <Button size="sm" onClick={onClose}>Close</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
